import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

type ConfiguredField = {
	fieldKey: string
	displayLabel: string
	displayOrder: number
}

export function CycleReportFiltersTab() {
	const { cycleId } = useParams<{ cycleId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const {
		data: availableFields,
		isLoading: isLoadingAvailable,
		isError: isAvailableFieldsError,
	} = useQuery({
		...trpc.admin.reportFilterConfig.availableFields.queryOptions({ cycleId: cycleId as string }),
		enabled: !!cycleId,
	})

	const {
		data: configuredFields,
		isLoading: isLoadingConfig,
		isError: isConfigError,
	} = useQuery({
		...trpc.admin.reportFilterConfig.list.queryOptions({ cycleId: cycleId as string }),
		enabled: !!cycleId,
	})

	const [localConfig, setLocalConfig] = useState<ConfiguredField[] | null>(null)

	const effectiveConfig: ConfiguredField[] =
		localConfig ??
		(configuredFields?.map((f) => ({
			fieldKey: f.fieldKey,
			displayLabel: f.displayLabel ?? '',
			displayOrder: f.displayOrder,
		})) ||
			[])

	const configuredKeys = new Set(effectiveConfig.map((f) => f.fieldKey))

	const unconfiguredFields = (availableFields || []).filter((f) => !configuredKeys.has(f.fieldKey))

	const handleAdd = (fieldKey: string, label: string) => {
		const next: ConfiguredField[] = [
			...effectiveConfig,
			{
				fieldKey,
				displayLabel: label,
				displayOrder: effectiveConfig.length,
			},
		]
		setLocalConfig(next)
	}

	const handleRemove = (fieldKey: string) => {
		const next = effectiveConfig
			.filter((f) => f.fieldKey !== fieldKey)
			.map((f, index) => ({ ...f, displayOrder: index }))
		setLocalConfig(next)
	}

	const handleLabelChange = (fieldKey: string, value: string) => {
		const next = effectiveConfig.map((f) => (f.fieldKey === fieldKey ? { ...f, displayLabel: value } : f))
		setLocalConfig(next)
	}

	const upsertMutation = useMutation({
		...trpc.admin.reportFilterConfig.upsert.mutationOptions(),
		onSuccess: () => {
			showToast('Report filter configuration saved')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.reportFilterConfig.list.queryKey({ cycleId: cycleId as string }),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.reportFilterConfig.availableFields.queryKey({ cycleId: cycleId as string }),
			})
			setLocalConfig(null)
		},
		onError: () => {
			showToast('Something went wrong, the configuration was not saved', 'error')
		},
	})

	const handleSave = () => {
		if (!cycleId) {
			showToast('Cannot save: cycle context is unavailable', 'error')
			return
		}
		upsertMutation.mutate({
			cycleId,
			fields: effectiveConfig.map((f) => ({
				fieldKey: f.fieldKey,
				displayLabel: f.displayLabel || undefined,
				displayOrder: f.displayOrder,
			})),
		})
	}

	const isLoading = isLoadingAvailable || isLoadingConfig
	const isError = isAvailableFieldsError || isConfigError

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	if (isError) {
		return (
			<div className='mt-6 px-6'>
				<Text variant='small' className='text-red-500'>
					Failed to load report filter configuration. Please refresh the page and try again.
				</Text>
			</div>
		)
	}

	return (
		<div className='mt-6 px-6 flex flex-col gap-8'>
			<div className='flex flex-col gap-4'>
				<div className='flex justify-between items-center'>
					<Text variant='large' weight='semibold'>
						Report Filter Configuration
					</Text>
					<Button onClick={handleSave} disabled={upsertMutation.isPending} isLoading={upsertMutation.isPending}>
						Save Configuration
					</Button>
				</div>
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
				<div className='flex flex-col gap-4'>
					<Text variant='medium' weight='semibold'>
						Available Fields
					</Text>
					{unconfiguredFields.length === 0 ? (
						<Text variant='small' className='text-gray-500'>
							All fields have been configured as filters.
						</Text>
					) : (
						<div className='flex flex-col gap-2'>
							{unconfiguredFields.map((field) => (
								<div
									key={field.fieldKey}
									className='flex items-center justify-between px-4 py-3 border border-neutral-100 rounded-lg bg-neutral-0'>
									<Text variant='small'>{field.label}</Text>
									<Button size='small' variant='secondary' onClick={() => handleAdd(field.fieldKey, field.label)}>
										Add
									</Button>
								</div>
							))}
						</div>
					)}
				</div>

				<div className='flex flex-col gap-4'>
					<Text variant='medium' weight='semibold'>
						Configured Filters
					</Text>
					{effectiveConfig.length === 0 ? (
						<Text variant='small' className='text-gray-500'>
							No filters configured. Add fields from the left panel.
						</Text>
					) : (
						<div className='flex flex-col gap-2'>
							{effectiveConfig.map((field) => {
								const originalField = availableFields?.find((f) => f.fieldKey === field.fieldKey)
								const defaultLabel = originalField?.label ?? field.fieldKey
								return (
									<div
										key={field.fieldKey}
										className='flex items-center gap-3 px-4 py-3 border border-neutral-100 rounded-lg bg-neutral-0'>
										<div className='flex flex-col gap-1 flex-1'>
											<Text variant='xsmall' className='text-gray-500'>
												{defaultLabel}
											</Text>
											<Input
												placeholder={`Display label (defaults to "${defaultLabel}")`}
												value={field.displayLabel}
												onChange={(e) => handleLabelChange(field.fieldKey, e.target.value)}
											/>
										</div>
										<Button size='small' variant='destructive' onClick={() => handleRemove(field.fieldKey)}>
											Remove
										</Button>
									</div>
								)
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
